import { useContext } from "react";
import { IntlProvider } from "react-intl";
import contactMessages from "../translate/contactMessages";
import Logo from "../images/contact.png";
import Me from "../images/MJ1_01.JPG";
import { LangContext } from "../App";
import { Helmet } from "react-helmet-async";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import LanguageIcon from "@mui/icons-material/Language";
import BusinessIcon from "@mui/icons-material/Business";
import { motion, AnimatePresence } from "framer-motion";
import { textVariants } from "../components/textVariants";
import {
  Box,
  Container,
  Grid,
  Card,
  Typography,
  Divider,
  Link as MuiLink,
} from "@mui/material";

const Contact = () => {
  const language = useContext(LangContext);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Allworks.pl - Michał Jaskulski",
            legalName: "Allworks.pl Michał Jaskulski",
            url: "https://michaljaskulski.pl",
            logo: "https://michaljaskulski.pl/logo600.png",
            description:
              "Tworzenie dedykowanych aplikacji webowych w React, PHP i usługi programistyczne oraz wsparcie IT dla firm.",
            address: {
              "@type": "PostalAddress",
              streetAddress: "ul. Brzozowa 14D",
              addressLocality: "Mikołów",
              postalCode: "43-190",
              addressCountry: "PL",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: "50.1678",
              longitude: "18.9056",
            },
            vatID: "PL6351848380",
            telephone: "+48602209858",
            email: "michal.jaskulski@allworks.pl",
            priceRange: "$$",
          })}
        </script>
        <title>Mikołów | Katowice | Śląskie</title>
        <meta name="description" content="Mikołów, Katowice, Śląskie" />
      </Helmet>

      <Box
        className="bodyBackground"
        sx={{
          backgroundColor: "#f8fafc",
          width: "100%",
          pt: { xs: 3, md: 4 }, // Stały, nieduży odstęp od góry (pod menu)
          pb: { xs: 3, md: 4 }, // Bezpieczny odstęp od dołu
          px: 2,
          boxSizing: "border-box",
          fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        }}
      >
        <IntlProvider locale={language} messages={contactMessages[language]}>
          <AnimatePresence>
            <Container
              component={motion.div}
              maxWidth="sm"
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={textVariants}
              disableGutters
            >
              <Grid container justifyContent="center">
                <Grid
                  item
                  xs={12}
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      backgroundColor: "#ffffff",
                      borderRadius: "20px",
                      p: 3,
                      boxShadow: "0 10px 25px -10px rgba(0,0,0,0.05)",
                      border: "1px solid #e2e8f0",
                      width: "100%",
                      maxWidth: "480px", // Kompaktowa szerokość
                    }}
                  >
                    <Box>
                      {/* Sekcja profilowa (Zdjęcie + Imię i Nazwisko) */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Box
                          component="img"
                          src={Me}
                          alt="Michał Jaskulski - Fullstack React Developer"
                          sx={{
                            width: "70px",
                            height: "70px",
                            borderRadius: "14px",
                            objectFit: "cover",
                            objectPosition: "center 25%",
                            boxShadow: "0 4px 10px -3px rgba(0,0,0,0.12)",
                            border: "2px solid #ffffff",
                          }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#4f46e5",
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              display: "block",
                              mb: 0.25,
                            }}
                          >
                            Fullstack Developer & IT Manager
                          </Typography>
                          <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                              fontSize: "20px",
                              fontWeight: 800,
                              color: "#0f172a",
                              letterSpacing: "-0.02em",
                              lineHeight: 1.2,
                            }}
                          >
                            Michał Jaskulski
                          </Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />

                      {/* Linki kontaktowe */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        <ContactLink
                          href="tel:+48602209858"
                          icon={<PhoneAndroidIcon fontSize="small" />}
                          text="+48 602 209 858"
                        />

                        <ContactLink
                          href="mailto:michal.jaskulski@allworks.pl"
                          icon={<AlternateEmailIcon fontSize="small" />}
                          text="michal.jaskulski@allworks.pl"
                        />

                        <ContactLink
                          href="https://michaljaskulski.pl"
                          target="_blank"
                          rel="noreferrer"
                          icon={<LanguageIcon fontSize="small" />}
                          text="michaljaskulski.pl"
                        />
                      </Box>
                    </Box>

                    {/* Stopka karty */}
                    <Box>
                      <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        {Logo && (
                          <Box
                            component="img"
                            src={Logo}
                            alt="Logo"
                            sx={{
                              height: "32px",
                              objectFit: "contain",
                            }}
                          />
                        )}
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              m: 0,
                              fontSize: "13px",
                              color: "#334155",
                              fontWeight: 500,
                              lineHeight: 1.3,
                            }}
                          >
                            <BusinessIcon
                              fontSize="small"
                              sx={{
                                color: "#94a3b8",
                                mr: 0.5,
                                verticalAlign: "text-bottom",
                              }}
                            />
                            43-190 Mikołów, ul. Brzozowa 14D
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              mt: 0.25,
                              fontSize: "11px",
                              color: "#64748b",
                              letterSpacing: "0.05em",
                              fontWeight: 500,
                            }}
                          >
                            NIP: 635 184 83 80
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </AnimatePresence>
        </IntlProvider>
      </Box>
    </>
  );
};

const ContactLink = ({ href, icon, text, target, rel }) => (
  <MuiLink
    href={href}
    target={target}
    rel={rel}
    underline="none"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 1.5,
      color: "#334155",
      fontSize: "14px",
      fontWeight: 600,
      transition: "color 0.2s ease",
      "&:hover": {
        color: "#4f46e5",
      },
    }}
  >
    <Box
      component="span"
      sx={{
        color: "#4f46e5",
        backgroundColor: "#eef2ff",
        p: 0.85,
        borderRadius: "8px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 4px rgba(79, 70, 229, 0.06)",
      }}
    >
      {icon}
    </Box>
    {text}
  </MuiLink>
);

export default Contact;
